import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import AxiosUrl from '../../AxiosUrl';
import styles from './HomeLayout.module.css';

/**
 * Online Store → Home page style (domains.homeLayout, used by SiteEjs stores).
 *   auto   — 1 live product: single-product landing page; otherwise the store home
 *   store  — always the normal store home (banners, categories, product rows)
 *   single — a landing page for one chosen product, with the order form on it
 */
const OPTIONS = [
    {
        mode: 'auto',
        title: 'Automatic',
        badge: 'Recommended',
        text: 'With only one product, the home page becomes a landing page for it. Add more products and it switches back to the normal store.',
    },
    {
        mode: 'single',
        title: 'Single product landing page',
        text: 'One product fills the home page: photos, price, description, FAQs, reviews and an order form right on the page. Best for selling one product with ads.',
    },
    {
        mode: 'store',
        title: 'Full store',
        text: 'The normal home page with banners, categories and product rows.',
    },
];

function storeUrl(storeData) {
    if (!storeData) return '';
    if (storeData.domain) return `${storeData.DomainSSL === false ? 'http' : 'https'}://${storeData.domain}`;
    if (storeData.SubDomain) return `https://${storeData.SubDomain}`;
    return '';
}

function HomeLayout({ storeData, uid, onSaved }) {
    const saved = useMemo(
        () => ({ mode: storeData?.homeLayout?.mode || 'auto', productId: storeData?.homeLayout?.productId || '' }),
        [storeData]
    );
    const [mode, setMode] = useState(saved.mode);
    const [productId, setProductId] = useState(saved.productId);
    const [products, setProducts] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setMode(saved.mode);
        setProductId(saved.productId);
    }, [saved]);

    useEffect(() => {
        if (!uid) return;
        AxiosUrl.get(`get-products/${uid}?page=1&limit=100`)
            .then((res) => {
                const list = (res.data?.data || res.data?.products || []).filter((p) => Number(p.productStatus) !== 1);
                setProducts(list);
            })
            .catch(() => setProducts([]));
    }, [uid]);

    // Single mode without a pick: the newest product (the backend does the same).
    const chosen = productId || (mode === 'single' && products[0] ? products[0]._id : '');
    const dirty = mode !== saved.mode || (mode === 'single' && chosen !== saved.productId);
    const base = storeUrl(storeData);
    const autoSingle = products.length === 1;

    const previewUrl = base
        ? mode === 'store' || (mode === 'auto' && !autoSingle)
            ? `${base}/?home=store`
            : `${base}/?home=single${mode === 'single' && chosen ? `&product=${chosen}` : ''}`
        : '';

    const save = async () => {
        if (mode === 'single' && !chosen) return toast.error('Add a product first');
        try {
            setSaving(true);
            const res = await AxiosUrl.post(`/update-domain-new/${uid}`, {
                homeLayout: { mode, productId: mode === 'single' ? chosen : productId },
            });
            if (res.data && res.data.error) throw new Error(res.data.error);
            toast.success('Home page style saved');
            if (onSaved) onSaved();
        } catch (err) {
            const d = err?.response?.data;
            toast.error((typeof d === 'string' && d) || d?.error || err.message || 'Could not save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.card}>
            <div className={styles.head}>
                <div>
                    <h2 className={styles.title}>Home page style</h2>
                    <p className={styles.sub}>Choose what customers see first when they open your store.</p>
                </div>
            </div>

            <div className={styles.options} role="radiogroup" aria-label="Home page style">
                {OPTIONS.map((o) => (
                    <label key={o.mode} className={`${styles.option} ${mode === o.mode ? styles.on : ''}`}>
                        <input
                            type="radio"
                            name="home-layout"
                            value={o.mode}
                            checked={mode === o.mode}
                            onChange={() => setMode(o.mode)}
                        />
                        <span className={styles.optBody}>
                            <span className={styles.optTitle}>
                                {o.title}
                                {o.badge && <span className={styles.badge}>{o.badge}</span>}
                            </span>
                            <span className={styles.optText}>{o.text}</span>
                            {o.mode === 'auto' && mode === 'auto' && (
                                <span className={styles.note}>
                                    {autoSingle
                                        ? 'You have 1 product, so your home page is the landing page now.'
                                        : `You have ${products.length} products, so your home page is the full store now.`}
                                </span>
                            )}
                        </span>
                    </label>
                ))}
            </div>

            {mode === 'single' && (
                <div className={styles.picker}>
                    <label htmlFor="hl-product">Product on the home page</label>
                    {products.length ? (
                        <select id="hl-product" value={chosen} onChange={(e) => setProductId(e.target.value)}>
                            {products.map((p) => (
                                <option key={p._id} value={p._id}>
                                    {p.productName} — ৳{p.priceSell}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <p className={styles.sub}>Add a product first.</p>
                    )}
                    <p className={styles.hint}>
                        The page uses this product's photos, price, compare price, description, FAQs and approved reviews.
                        Add those on the product to make the page stronger.
                    </p>
                </div>
            )}

            <div className={styles.actions}>
                {previewUrl && (
                    <a className={styles.btnSecondary} href={previewUrl} target="_blank" rel="noopener noreferrer">
                        Preview
                    </a>
                )}
                <button type="button" className={styles.btnPrimary} onClick={save} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    );
}

export default HomeLayout;
